import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { FaTimes, FaBars } from "react-icons/fa";
import brandLogo from "../assets/brandLogo.png";

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const [activeSection, setActiveSection] = useState<string>("");

  // Navigation links for both desktop and mobile
  const navLinks = [
    { label: "Loans", href: "/" },
    { label: "Eligibility", href: "/#eligibility" },
    { label: "Calculator", href: "/#loan-calculator" },
    { label: "About Us", href: "/about" },
    { label: "Contact", href: "/contact" },
    // { label: "FAQs", href: "/faq" }
  ];

  useEffect(() => {
    if (location.pathname !== "/") {
      setActiveSection("");
      return;
    }

    // Set active section on initial load or click if hash is present
    if (location.hash) {
      const hashId = location.hash.replace("#", "");
      setActiveSection(hashId);
      return;
    }

    const sections = ["loan-calculator", "eligibility"];
    const observerOptions = {
      root: null,
      rootMargin: "-30% 0px -40% 0px", // Trigger when section occupies center of viewport
      threshold: 0
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    sections.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    const handleScroll = () => {
      // If close to the top, reset active section to "Loans"
      if (window.scrollY < 180) {
        setActiveSection("");
      }
    };
    window.addEventListener("scroll", handleScroll);

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", handleScroll);
    };
  }, [location.pathname, location.hash]);

  const isLinkActive = (href: string) => {
    if (location.pathname === "/") {
      if (href === "/") return activeSection === "";
      if (href === "/#eligibility") return activeSection === "eligibility";
      if (href === "/#loan-calculator") return activeSection === "loan-calculator";
      return false;
    }
    return location.pathname === href;
  };

  return (
    <nav className="w-full fixed top-0 left-0 z-[100]" style={{background: 'linear-gradient(90deg, #F6FAFF 0%, #EAF3FB 100%)'}}>
      <div className="max-w-[1500px] mx-auto px-6 xl:px-12 2xl:px-20 py-4 flex items-center justify-between relative">
        {/* Logo and Brand */}
        <a href="/" className="flex items-center gap-3 min-w-[180px]">
          <img
            className="h-11 w-auto object-contain"
            src={brandLogo}
            alt={import.meta.env.VITE_COMPANY_NAME}
          />
        </a>

        {/* Desktop Navigation - Centered */}
        <div className="hidden xl:flex flex-1 justify-center items-center xl:gap-5 2xl:gap-10 xl:mx-8 2xl:mx-12 whitespace-nowrap">
          {navLinks.map((item) => {
            const active = isLinkActive(item.href);
            return (
              <a
                key={item.label}
                href={item.href}
                className={`relative font-semibold text-[16px] px-1 pb-1 transition-colors ${active ? 'text-[var(--primary)]' : 'text-[#334155] hover:text-[var(--primary)]'}`}
                style={{ fontFamily: 'inherit' }}
              >
                {item.label}
                {active && (
                  <span className="absolute bottom-[-6px] left-0 right-0 h-[3px] bg-[var(--primary)] rounded" />
                )}
              </a>
            );
          })}
        </div>

        {/* Desktop Right Action Buttons */}
        <div className="hidden xl:flex items-center gap-3">
          <a
            href={`${import.meta.env.VITE_WEBSITE_LINK}/login`}
            target="_blank"
            rel="noopener noreferrer"
            className="border border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-white px-7 py-2.5 rounded-[8px] font-semibold text-[15px] transition-all active:scale-95 text-center"
            style={{ minWidth: 110 }}
          >
            Login
          </a>
          <a
            href={`${import.meta.env.VITE_WEBSITE_LINK}/repay-now`}
            target="_blank"
            rel="noopener noreferrer"
            className="border border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-white px-7 py-2.5 rounded-[8px] font-semibold text-[15px] transition-all active:scale-95 text-center"
            style={{ minWidth: 130 }}
          >
            Repay Now
          </a>
          <a
            href={import.meta.env.VITE_WEBSITE_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white px-7 py-2.5 rounded-[8px] font-semibold text-[15px] shadow-[0_4px_12px_rgba(0,150,255,0.2)] transition-all active:scale-95 text-center"
            style={{ minWidth: 130 }}
          >
            Apply Now
          </a>
        </div>

        {/* Mobile Menu Trigger */}
        <button
          className="xl:hidden relative z-[101] text-[#111] p-2 -mr-2"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle Menu"
        >
          {menuOpen ? <FaTimes className="text-2xl" /> : <FaBars className="text-2xl" />}
        </button>

        {/* Mobile Drawer */}
        <div
          className={`fixed inset-0 top-0 left-0 w-full h-screen bg-[#F6FAFF] z-[100] p-6 pt-24 flex flex-col gap-2 xl:hidden transition-all duration-500 ease-in-out ${
            menuOpen ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
          }`}
        >
          <div className="flex flex-col gap-2">
            {navLinks.map((item) => {
              const active = isLinkActive(item.href);
              return (
                <a
                  key={item.label}
                  href={item.href}
                  className={`w-full font-bold text-xl py-4 border-b border-[var(--border-light)] flex items-center justify-between group ${active ? 'text-[var(--primary)]' : 'text-[#111] hover:text-[var(--primary)]'}`}
                  onClick={() => setMenuOpen(false)}
                >
                  {item.label}
                  {active && (
                    <span className="block h-[3px] w-8 bg-[var(--primary)] rounded ml-2" />
                  )}
                </a>
              );
            })}
          </div>

          <div className="mt-auto pb-10 flex flex-col gap-3">
            <a
              href={`${import.meta.env.VITE_WEBSITE_LINK}/login`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-center py-5 rounded-xl border border-[var(--primary)] text-[var(--primary)] font-bold text-xl active:scale-95 transition-all"
              style={{ minWidth: 140 }}
            >
              Login
            </a>
            <a
              href={`${import.meta.env.VITE_WEBSITE_LINK}/repay-now`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-center py-5 rounded-xl border border-[var(--primary)] text-[var(--primary)] font-bold text-xl active:scale-95 transition-all"
              style={{ minWidth: 140 }}
            >
              Repay Now
            </a>
            <a
              href={import.meta.env.VITE_WEBSITE_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-center py-5 rounded-xl bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white font-bold text-xl shadow-none active:scale-95 transition-all"
              style={{ minWidth: 140 }}
            >
              Apply Now
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
